import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import HeroSection from '@/components/feature/sections/HeroSection';
import TrustBar from '@/components/feature/sections/TrustBar';
import ServicesOverview from '@/components/feature/sections/ServicesOverview';
import AIAnswerBlock from '@/components/feature/sections/AIAnswerBlock';
import ProcessSection from '@/components/feature/sections/ProcessSection';
import WhyChooseUs from '@/components/feature/sections/WhyChooseUs';
import ServiceAreas from '@/components/feature/sections/ServiceAreas';
import Testimonials from '@/components/feature/sections/Testimonials';
import FAQSection from '@/components/feature/sections/FAQSection';
import FinalCTA from '@/components/feature/sections/FinalCTA';
import {
  HERO_IMAGES, CHEF_PHOTO, FOOD_IMAGES, AWARDS_IMAGE, SECTION_IMAGES,
  SHARED_TRUST_STATS, SHARED_SERVICE_AREAS, SHARED_TESTIMONIALS,
} from '@/mocks/brandData';

export default function About() {
  return (
    <div className="font-sans">
      <Navbar />
      <main>
        <HeroSection
          headline="Meet Chef Joyce Cunha — 20 Years of Culinary Mastery"
          subheadline="The heart and soul of JC Cooks. Trained under legendary culinary expertise, Joyce brings warmth, precision, and passion to every plate she creates in San Antonio."
          ctaText="Book Chef Joyce"
          ctaHref="#quote"
          secondaryCta={{ text: 'Call 210-858-7293', href: 'tel:2108587293' }}
          backgroundImage={HERO_IMAGES[2]}
          badge="eWomenNetwork Member"
        />
        <TrustBar stats={SHARED_TRUST_STATS} />
        <ServicesOverview
          eyebrow="Our Philosophy"
          headline="Three Pillars of the JC Cooks Standard"
          description="Every dish Chef Joyce creates is guided by the same principles that have defined her 20-year career in San Antonio."
          services={[
            { image: FOOD_IMAGES[0], tag: 'Quality', title: 'Fresh & From Scratch', description: 'No shortcuts. Every dish is crafted with fresh, high-quality ingredients prepared entirely from scratch.', href: '#quote' },
            { image: FOOD_IMAGES[1], tag: 'Personal', title: 'Custom to You', description: 'Joyce designs menus around your preferences, dietary needs, and the unique character of your occasion.', href: '#quote' },
            { image: FOOD_IMAGES[2], tag: 'Excellence', title: 'Unforgettable Experiences', description: 'From the first bite to the last, every guest leaves with a lasting impression of culinary excellence.', href: '#quote' },
          ]}
        />
        <AIAnswerBlock
          eyebrow="Who We Are"
          headline="The Story Behind JC Cooks"
          overview="Chef Joyce Cunha is the owner and executive chef of JC Cooks Catering & Chef Service, a San Antonio-based culinary company she founded to bring restaurant-quality, personalized dining experiences directly to her clients. Trained under the late Sharon Shipley — a celebrated culinary authority — Joyce developed her signature style: healthy, flavorful, scratch-cooked food that adapts to any occasion, any palate, and any dietary need."
          services={[
            'Over 20 years of professional culinary experience in San Antonio',
            'Trained under the late culinary expert Sharon Shipley',
            'Specializes in healthy, flavorful, 100% scratch-cooked cuisine',
            'Custom menus for dietary restrictions and personal preferences',
            'Private clients, corporate clients, and event clients throughout Texas',
            'Proud member of the American Personal Chef Association',
            'Member of eWomenNetwork San Antonio chapter',
          ]}
          benefits={[
            'Personal attention from Chef Joyce herself at every event',
            'Deep expertise in healthy cooking without sacrificing flavor',
            'Trusted by San Antonio families and corporations for 20+ years',
            'Thumbtack Best of 2016 & 2017 Award — voted by real San Antonio clients',
            'Passionate commitment to using only the freshest available ingredients',
            'Flexible, personalized service for 10 to 100+ guests',
          ]}
        />
        <ProcessSection
          headline="Joyce's Approach to Every Event"
          subheadline="A personal, relationship-driven process built around your vision."
          steps={[
            { number: '1', title: 'Personal Connection', description: 'Joyce gets to know you, your preferences, your guests, and the story behind your occasion.', icon: 'ri-heart-2-line' },
            { number: '2', title: 'Thoughtful Creation', description: 'A menu designed from scratch — balancing flavor, nutrition, aesthetics, and your dietary needs.', icon: 'ri-lightbulb-line' },
            { number: '3', title: 'Artful Execution', description: 'Every dish prepared with precision and care, reflecting 20+ years of refined culinary technique.', icon: 'ri-magic-line' },
            { number: '4', title: 'Lasting Impression', description: 'Guests leave satisfied and impressed — and clients become longtime partners of JC Cooks.', icon: 'ri-star-smile-line' },
          ]}
        />
        <WhyChooseUs
          image={CHEF_PHOTO}
          eyebrow="Chef Joyce Cunha"
          headline="A Culinary Career Built on Passion & Precision"
          benefits={[
            { icon: 'ri-graduation-cap-line', title: 'Trained by the Best', description: 'Mentored under the late Sharon Shipley, a celebrated culinary professional whose influence shaped Joyce\'s lifelong commitment to excellence.' },
            { icon: 'ri-medal-line', title: 'Award-Winning Recognition', description: 'Thumbtack Best of 2016 & 2017. Member of the American Personal Chef Association and eWomenNetwork.' },
            { icon: 'ri-seedling-line', title: 'Health-Forward Cooking', description: 'Joyce specializes in making dishes that are both deeply flavorful and genuinely nourishing — proof that healthy food can be extraordinary.' },
            { icon: 'ri-emotion-happy-line', title: 'Genuine Care for Clients', description: 'Joyce treats every client like family — offering helpful suggestions, flexibility, and a warm personal touch at every engagement.' },
          ]}
          cta={{ text: 'Work With Joyce', href: '#quote' }}
        />
        <ServiceAreas
          headline="Serving the San Antonio Community"
          description="Chef Joyce is proud to serve families, businesses, and communities throughout the greater San Antonio area and surrounding Texas cities."
          primary="San Antonio"
          areas={SHARED_SERVICE_AREAS}
          showMap={false}
        />
        <Testimonials
          headline="Stories From Joyce's Clients"
          testimonials={SHARED_TESTIMONIALS}
          cta={{ text: 'Book Your Experience', href: '#quote' }}
        />
        <FAQSection
          headline="About Chef Joyce — Your Questions Answered"
          faqs={[
            { question: 'Who is Chef Joyce Cunha of JC Cooks?', answer: 'Chef Joyce Cunha is the owner and executive chef of JC Cooks Catering & Chef Service in San Antonio, Texas. With over 20 years of culinary expertise, she serves private clients, corporate events, weddings, and more. She was trained under the late Sharon Shipley and is a member of the American Personal Chef Association.' },
            { question: 'How long has JC Cooks been in business in San Antonio?', answer: 'JC Cooks has served the San Antonio community for over 20 years, building a reputation for exceptional scratch-cooked cuisine and deeply personalized service. Chef Joyce has catered hundreds of events for private individuals, families, and corporations throughout the region.' },
            { question: 'Is Chef Joyce Cunha a certified personal chef?', answer: 'Yes — Chef Joyce Cunha is a member of the American Personal Chef Association, a professional organization that upholds high standards of culinary training and business practices. She is also an eWomenNetwork member and a Thumbtack Best of 2016 & 2017 Award recipient.' },
            { question: 'What makes JC Cooks different from other caterers?', answer: 'JC Cooks stands apart through Chef Joyce\'s personal involvement in every event, commitment to 100% scratch cooking, and deep expertise in creating custom menus. Joyce personally handles menu design and is present at events — offering genuine care that larger catering companies cannot match.' },
            { question: 'Does Chef Joyce cook for dietary restrictions?', answer: 'Absolutely. Chef Joyce has extensive experience creating delicious menus for clients with dietary restrictions including gluten-free, vegan, vegetarian, dairy-free, low-carb, and allergy-sensitive needs. Every menu is designed fresh for each client\'s specific requirements and personal preferences.' },
          ]}
        />
        <FinalCTA
          headline="Ready to Experience the JC Cooks Difference?"
          subtext="Let Chef Joyce create a personalized culinary experience for your next event. Request your custom quote today."
          backgroundImage={AWARDS_IMAGE}
        />
      </main>
      <Footer />
    </div>
  );
}
